/**
 * One time range for the whole panel.
 *
 * The old panel had four different answers to "what window am I looking at":
 * Overview and Audit had a picker defaulting to 24h, Catalog hard-coded 30
 * days, Try-on and MAHI each had their own `days` selector, and Users had no
 * control at all. So the same question asked on two pages returned two
 * non-comparable numbers, and nobody could tell.
 *
 * This provider is the single source. It lives in the URL (`?range=7d`, or
 * `?since=…&until=…`) so a range survives navigation between sections, a
 * refresh, and being pasted into Slack — an admin sharing "look at this" and
 * the recipient seeing a different window is the same bug in a different hat.
 *
 * It exposes BOTH shapes the backend already speaks:
 *   - `since`/`until` ISO strings — /overview, /audit
 *   - `days` integer            — /catalog/*, /tryon/*, /mahi/*
 * so that no existing endpoint signature has to change to be driven by it.
 */
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const PRESETS = [
  { key: '24h', label: 'Last 24 hours', short: '24h', hours: 24, days: 1 },
  { key: '7d', label: 'Last 7 days', short: '7d', hours: 24 * 7, days: 7 },
  { key: '30d', label: 'Last 30 days', short: '30d', hours: 24 * 30, days: 30 },
  { key: '90d', label: 'Last 90 days', short: '90d', hours: 24 * 90, days: 90 },
];

const DEFAULT_KEY = '7d';

const Ctx = createContext(null);

function clampDays(n) {
  // 180 is the ceiling every `days` endpoint declares (Query(ge=1, le=180)).
  // Clamping here rather than letting the request 422 keeps a wide custom
  // range degrading to "as much as we hold" instead of to an error toast.
  return Math.min(180, Math.max(1, Math.ceil(n)));
}

export function RangeProvider({ children }) {
  const [params, setParams] = useSearchParams();

  const value = useMemo(() => {
    const sinceParam = params.get('since');
    const untilParam = params.get('until');

    if (sinceParam && untilParam) {
      const since = new Date(sinceParam);
      const until = new Date(untilParam);
      if (!Number.isNaN(since.getTime()) && !Number.isNaN(until.getTime()) && since < until) {
        return {
          key: 'custom',
          label: 'Custom range',
          short: 'Custom',
          since: since.toISOString(),
          until: until.toISOString(),
          days: clampDays((until - since) / 86_400_000),
        };
      }
    }

    const key = params.get('range') || DEFAULT_KEY;
    const preset = PRESETS.find((p) => p.key === key) || PRESETS.find((p) => p.key === DEFAULT_KEY);
    const until = new Date();
    const since = new Date(until.getTime() - preset.hours * 3_600_000);
    return {
      key: preset.key,
      label: preset.label,
      short: preset.short,
      since: since.toISOString(),
      until: until.toISOString(),
      days: preset.days,
    };
  }, [params]);

  // `setParams` with a function keeps every other query param (a table's
  // sort, a selected tab) intact — changing the range must not silently
  // reset the rest of the page's state.
  const setPreset = useCallback((key) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('range', key);
      next.delete('since');
      next.delete('until');
      return next;
    }, { replace: true });
  }, [setParams]);

  const setCustom = useCallback((sinceISO, untilISO) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('since', sinceISO);
      next.set('until', untilISO);
      next.delete('range');
      return next;
    }, { replace: true });
  }, [setParams]);

  const ctx = useMemo(() => ({ ...value, setPreset, setCustom }), [value, setPreset, setCustom]);

  return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
}

export function useRange() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useRange must be used inside <RangeProvider>');
  return ctx;
}

/**
 * Preserves the active range when building an internal link, so a deep-link
 * out of a Home tile lands on the destination showing the same window the
 * tile was counting. Without this, every drill-down silently resets to the
 * default and the number on the next page doesn't match the one just clicked.
 */
export function withRange(path, range) {
  if (!range) return path;
  const qs = new URLSearchParams();
  if (range.key === 'custom') {
    qs.set('since', range.since);
    qs.set('until', range.until);
  } else if (range.key !== DEFAULT_KEY) {
    qs.set('range', range.key);
  }
  const s = qs.toString();
  if (!s) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${s}`;
}
